import {
  BALANCE_DELTA,
  POSITION,
  PositionAssetTypeEnum,
  PositionUpdateSourceEnum,
  ROLE,
  resolvePositionUnderlying,
  useCombinedErrorObs,
  useMarketAccountsContext,
  useSecuritiesContext,
  useSubscription,
  useSyncedRef,
  wsMerge,
  type MinimalSubscriptionResponse,
  type Position,
  type WebsocketRequest,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { map, type Observable } from 'rxjs';
import { isUserRole, useUser } from '../../../../hooks';
import { useDisplaySettings } from '../../../../providers/DisplaySettingsProvider';
import type { Balance } from '../../../../types';
import { useEnrichDeltaBalancesPipe } from '../../BalancesV2/useEnrichedDeltaBalancesObs';
import { POSITIONS_BLOTTER_CONVERSION_TOLERANCE } from '../tokens';
import type { PositionsTableFilter } from '../types';
import { UnifiedPosition } from './UnifiedPosition';
import {
  getUnifiedSourceRequestTag,
  resolveUnifiedSourceStreamConfig,
  selectUnifiedSourceStreams,
} from './unifiedSourceFilter';
import { toUnifiedServerFilterQuery, type UnifiedServerFilterQuery } from './usePositionsUnifiedFilter';

interface PositionsRequest extends WebsocketRequest, UnifiedServerFilterQuery {
  ShowZeroBalances: boolean;
  EquivalentCurrency: string;
  Tolerance: string;
  Source: PositionUpdateSourceEnum;
}

interface BalanceRequest extends WebsocketRequest, UnifiedServerFilterQuery {
  ShowZeroBalances: boolean;
  EquivalentCurrency: string;
  Source: PositionUpdateSourceEnum;
}

interface UseUnifiedPositionsBalancesObsParams {
  tag: string;
  showZeroBalances: boolean;
  filter: PositionsTableFilter;
}

/**
 * This hook makes POSITION and/or BALANCE_DELTA requests depending on the selected source filter,
 * then merges the resulting streams into UnifiedPosition rows.
 */
export const useUnifiedPositionsBalancesObs = ({
  tag,
  showZeroBalances,
  filter,
}: UseUnifiedPositionsBalancesObsParams) => {
  const user = useUser();
  const { homeCurrency } = useDisplaySettings();
  const { marketAccountsByID, marketAccountsByName } = useMarketAccountsContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const marketAccountsByIDRef = useSyncedRef(marketAccountsByID);
  const marketAccountsByNameRef = useSyncedRef(marketAccountsByName);
  const securitiesBySymbolRef = useSyncedRef(securitiesBySymbol);
  const isTalosAdmin = isUserRole(user, ROLE.TALOS_ADMIN);

  const streamConfig = useMemo(
    () => resolveUnifiedSourceStreamConfig(filter.Source, isTalosAdmin),
    [filter.Source, isTalosAdmin]
  );
  const requestTag = useMemo(
    () => getUnifiedSourceRequestTag(tag, filter.Source, isTalosAdmin),
    [tag, filter.Source, isTalosAdmin]
  );
  const [internalPositionsRequest, setInternalPositionsRequest] = useState<PositionsRequest | null>(null);
  const [gatewayPositionsRequest, setGatewayPositionsRequest] = useState<PositionsRequest | null>(null);
  const [gatewayBalancesRequest, setGatewayBalancesRequest] = useState<BalanceRequest | null>(null);

  useEffect(() => {
    const serverFilter = toUnifiedServerFilterQuery(filter);
    const baseRequest = {
      tag: requestTag,
      ShowZeroBalances: showZeroBalances,
      EquivalentCurrency: homeCurrency,
      Throttle: '1s',
      ...serverFilter,
    };

    setInternalPositionsRequest(currentRequest => {
      const maybeNewRequest: PositionsRequest | null = streamConfig.subscribeInternalPositions
        ? {
            ...baseRequest,
            name: POSITION,
            Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
            Source: PositionUpdateSourceEnum.Internal,
          }
        : null;
      return isEqual(currentRequest, maybeNewRequest) ? currentRequest : maybeNewRequest;
    });

    setGatewayPositionsRequest(currentRequest => {
      const maybeNewRequest: PositionsRequest | null = streamConfig.subscribeGatewayPositions
        ? {
            ...baseRequest,
            name: POSITION,
            Tolerance: POSITIONS_BLOTTER_CONVERSION_TOLERANCE,
            Source: PositionUpdateSourceEnum.Gateway,
          }
        : null;
      return isEqual(currentRequest, maybeNewRequest) ? currentRequest : maybeNewRequest;
    });

    setGatewayBalancesRequest(currentRequest => {
      const maybeNewRequest: BalanceRequest | null = streamConfig.subscribeGatewayBalances
        ? {
            ...baseRequest,
            name: BALANCE_DELTA,
            Source: PositionUpdateSourceEnum.Gateway,
          }
        : null;
      return isEqual(currentRequest, maybeNewRequest) ? currentRequest : maybeNewRequest;
    });
  }, [requestTag, showZeroBalances, homeCurrency, filter, streamConfig]);

  const { data: internalPositionsObs, error: internalPositionsErrorObs } = useSubscription<Position>(
    internalPositionsRequest,
    { replay: false }
  );
  const { data: gatewayPositionsObs, error: gatewayPositionsErrorObs } = useSubscription<Position>(
    gatewayPositionsRequest,
    { replay: false }
  );
  const { data: gatewayBalancesObs, error: gatewayBalancesErrorObs } = useSubscription<Balance>(
    gatewayBalancesRequest,
    {
      replay: false,
    }
  );

  const enrichedInternalPositionsObs = useMemo(
    () => enrichPositionsObs(internalPositionsObs, marketAccountsByNameRef, securitiesBySymbolRef),
    [internalPositionsObs, marketAccountsByNameRef, securitiesBySymbolRef]
  );

  const enrichedGatewayPositionsObs = useMemo(
    () => enrichPositionsObs(gatewayPositionsObs, marketAccountsByNameRef, securitiesBySymbolRef),
    [gatewayPositionsObs, marketAccountsByNameRef, securitiesBySymbolRef]
  );

  const enrichBalancesPipe = useEnrichDeltaBalancesPipe();
  const enrichedGatewayBalancesObs = useMemo(
    () => gatewayBalancesObs.pipe(enrichBalancesPipe),
    [gatewayBalancesObs, enrichBalancesPipe]
  );

  // Need to turn received BalanceDelta into Position
  const gatewayBalancesMappedToPositionsObs: Observable<MinimalSubscriptionResponse<UnifiedPosition, string>> = useMemo(
    () =>
      enrichedGatewayBalancesObs.pipe(
        map(message => {
          const newData: UnifiedPosition[] = [];
          for (const balance of message.data) {
            const marketAccount = marketAccountsByIDRef.current.get(balance.MarketAccountID);
            if (!marketAccount) {
              continue;
            }

            newData.push(
              new UnifiedPosition({
                balance,
                metadata: {
                  marketAccountName: marketAccount.Name,
                  marketName: marketAccount.Market,
                  marketAccountGroup: marketAccount.Group,
                  underlying:
                    resolvePositionUnderlying({
                      asset: balance.Currency,
                      assetType: PositionAssetTypeEnum.Spot,
                    }) ?? 'Unknown',
                },
              })
            );
          }

          return {
            ...message,
            data: newData,
          };
        })
      ),
    [enrichedGatewayBalancesObs, marketAccountsByIDRef]
  );

  const unifiedObs = useMemo(
    () =>
      wsMerge({
        sources: selectUnifiedSourceStreams(streamConfig, {
          internalPositions: enrichedInternalPositionsObs,
          gatewayPositions: enrichedGatewayPositionsObs,
          gatewayBalances: gatewayBalancesMappedToPositionsObs,
        }),
        getUniqueKey: item => item.rowID,
      }),
    [streamConfig, enrichedInternalPositionsObs, enrichedGatewayPositionsObs, gatewayBalancesMappedToPositionsObs]
  );

  const errorObservable = useCombinedErrorObs(
    selectUnifiedSourceStreams(streamConfig, {
      internalPositions: internalPositionsErrorObs,
      gatewayPositions: gatewayPositionsErrorObs,
      gatewayBalances: gatewayBalancesErrorObs,
    })
  );

  return { dataObservable: unifiedObs, errorObservable };
};

function enrichPositionsObs(
  positionsObs: Observable<MinimalSubscriptionResponse<Position, string>>,
  marketAccountsByNameRef: ReturnType<
    typeof useSyncedRef<Map<string, { Name: string; Market: string; Group?: string }>>
  >,
  securitiesBySymbolRef: ReturnType<typeof useSyncedRef<Map<string, { BaseCurrency?: string }>>>
) {
  return positionsObs.pipe(
    map(message => {
      const newData: UnifiedPosition[] = [];
      for (const position of message.data) {
        const marketAccount = marketAccountsByNameRef.current.get(position.MarketAccount);
        if (!marketAccount) {
          continue;
        }

        const underlying =
          resolvePositionUnderlying({
            asset: position.Asset,
            assetType: position.AssetType,
            securitiesBySymbol: securitiesBySymbolRef.current,
          }) ?? 'Unknown';
        newData.push(
          new UnifiedPosition({
            position,
            metadata: {
              marketAccountName: marketAccount.Name,
              marketName: marketAccount.Market,
              marketAccountGroup: marketAccount.Group,
              underlying,
            },
          })
        );
      }

      return {
        ...message,
        data: newData,
      };
    })
  );
}
